import React, { useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import { Box, Card, CardContent, Typography, Grid, Divider, Tooltip, makeStyles } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import axios from 'axios';
import { Header, Progress } from '@backstage/core-components';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import './Dashboard.css';

type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

type TxFlowData = {
  clientName: string;
  activityState: string;
  activationTime: number;
  bindTime: number;
  storeAndForwardAckedMsgCount: number;
  ackedMsgCount: number;
  unackedMsgCount: number;
};

type QueueData = {
  collections: {
    msgs: {
      count: number;
    };
  };
  data: {
    queueName: string;
    hasResourceInformationConflict?: boolean;
    accessType: string;
    msgSpoolUsage: number;
    spooledMsgCount: number;
    maxBindCount: number;
    rxByteRate: number;
    rxMsgRate: number;
    txByteRate: number;
    txMsgRate: number;
    maxTtlExpiredDiscardedMsgCount: number;
    averageRxByteRate: number;
    averageRxMsgRate: number;
    averageTxByteRate: number;
    averageTxMsgRate: number;
    maxTtl: number;
    respectTtlEnabled: boolean;
    maxMsgSpoolUsage: number;
    txFlowsData: {
      data: TxFlowData[];
    };
  };
};

const useStyles = makeStyles(theme => ({
  conflictIndicator: {
    alignItems: 'center',
    backgroundColor: '#ED6C02',
    borderRadius: theme.shape.borderRadius,
    color: '#FFFFFF',
    display: 'inline-flex',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1.5),
    padding: theme.spacing(0.5, 1),
    verticalAlign: 'middle',
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const { t, formatNumber, formatUtcDateTime } =
    useServiceRegistryTranslation();
  const [userRole, setUserRole] = useState<UserRoleResponse | null>(null);
  const identityApi = useApi(identityApiRef);
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(true);
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const [queueData, setQueueData] = useState<QueueData[]>([]);
  const [data, setData] = useState({
    name: '',
    localStatus: '',
    operational: '',
    host: '',
    connections: 0,
    connectionsServiceAmqp: 0,
    maxConnectionsServiceAmqp: 0,
    maxConnections: 0,
    currentIngressByteRatePerSecond: 0,
    currentIngressRatePerSecond: 0,
    currentEgressByteRatePerSecond: 0,
    currentEgressRatePerSecond: 0,
    averageIngressByteRatePerSecond: 0,
    averageIngressRatePerSecond: 0,
    averageEgressByteRatePerSecond: 0,
    averageEgressRatePerSecond: 0,
  });
  const [spoolData, setSpoolData] = useState({
    operationalStatus: '',
    configStatus: '',
    maxDiskUsage: '',
    currentPersistUsage: '',
  });
  const [error, setError] = useState('');

  const retry = async (fn: () => Promise<any>, retries = 3, delay = 1000) => {
    try {
      return await fn();
    } catch (error) {
      if (retries === 0) throw error;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retry(fn, retries - 1, delay);
    }
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        setLoading(true);
        const profile = await identityApi.getProfileInfo();
        const { token } = await identityApi.getCredentials();
        const response = await fetch(`${baseUrl}/api/users/Role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: profile.email }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user role: ${response.statusText}`);
        }

        const userRoleResponse: UserRoleResponse = await response.json();
        setUserRole(userRoleResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { token } = await identityApi.getCredentials();
        const apiUrl = `${baseUrl}/api/dashboard/dashboard-data`;
        
        const response = await retry(() =>
          axios.post(apiUrl, {}, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            timeout: 15000,
          }),
        );

        // Now the response is already filtered by the backend
        const { vpnData, spoolData: spoolInfo } = response.data;

        if (vpnData && spoolInfo) {
          setData({
            name: vpnData.name || 'Unknown',
            localStatus: vpnData['local-status'] || 'Unknown',
            operational: vpnData.operational ? 'True' : 'False',
            host: new URL(baseUrl).host,
            connections: vpnData.connections || 0,
            connectionsServiceAmqp: vpnData['connections-service-amqp'] || 0,
            maxConnectionsServiceAmqp: vpnData['max-connections-service-amqp'] || 0,
            maxConnections: vpnData['max-connections'] || 0,
            currentIngressByteRatePerSecond: vpnData['current-ingress-byte-rate-per-second'] || 0,
            currentIngressRatePerSecond: vpnData['current-ingress-rate-per-second'] || 0,
            currentEgressByteRatePerSecond: vpnData['current-egress-byte-rate-per-second'] || 0,
            currentEgressRatePerSecond: vpnData['current-egress-rate-per-second'] || 0,
            averageIngressByteRatePerSecond: vpnData['average-ingress-byte-rate-per-minute'] || 0,
            averageIngressRatePerSecond: vpnData['average-ingress-rate-per-minute'] || 0,
            averageEgressByteRatePerSecond: vpnData['average-egress-byte-rate-per-minute'] || 0,
            averageEgressRatePerSecond: vpnData['average-egress-rate-per-minute'] || 0,
          });

          setSpoolData({
            operationalStatus: spoolInfo['operational-status'] || 'Unknown',
            configStatus: spoolInfo['config-status'] || 'Unknown',
            maxDiskUsage: spoolInfo['max-disk-usage'] || 'Unknown',
            currentPersistUsage: spoolInfo['current-persist-usage'] || 'Unknown',
          });
        } else {
          setError(t('dashboard.loadFailed'));
        }
      } catch (err) {
        setError(t('dashboard.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchQueueData = async () => {
      try {
        const { token } = await identityApi.getCredentials();

        const response = await retry(() =>
          axios.post(
            `${baseUrl}/api/dashboard/queue-details`, {},
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            },
          ),
        );

        // Response is already filtered by the backend
        setQueueData(response.data);
      } catch (err) {
        setError(t('dashboard.loadFailed'));
      } finally {
        setQueueLoading(false);
      }
    };

    fetchQueueData();
  }, []);

  // Rest of your component remains the same...
  if (loading || queueLoading) {
    return <Progress />;
  }
  
  if (error) {
    return (
      <Paper className="paper" elevation={3}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  const getGaugeValue = (value: number, max: number) => {
    return max ? (value / max) * 100 : 0;
  };

  // Rest of your JSX remains exactly the same...
  return (
    <>
      <Header title={t('dashboard.title')} />
      <div className="root">
        <Paper className="paper" elevation={3}>
          <Typography className="header">{t('dashboard.name')}</Typography>
          <Typography variant="h6" className="field">
            {data.name}
          </Typography>
        </Paper>
        <Paper className="paper" elevation={3}>
          <Typography className="header">{t('dashboard.vpnStatus')}</Typography>
          <Typography
            variant="h6"
            className={`field ${
              data.localStatus === 'Up' ? 'statusUp' : 'statusDown'
            }`}
          >
            {data.localStatus}
          </Typography>
        </Paper>
        <Paper className="paper" elevation={3}>
          <Typography className="header">{t('dashboard.operationalStatus')}</Typography>
          <Typography variant="h6" className="field">
            {data.operational}
          </Typography>
        </Paper>
        <Paper className="paper" elevation={3}>
          <Typography className="header">{t('dashboard.instance')}</Typography>
          <Typography variant="h6" className="field">
            {data.host}
          </Typography>
        </Paper>

        {(userRole?.roleName === 'Admin' ||
          userRole?.roleName === 'Contract Authority') && (
          <>
            <Paper className="paper" elevation={3}>
              <Typography className="header">
                {t('dashboard.spoolOperationalStatus')}
              </Typography>
              <Typography variant="h6" className="field">
                {spoolData.operationalStatus}
              </Typography>
            </Paper>
            <Paper className="paper" elevation={3}>
              <Typography className="header">
                {t('dashboard.spoolConfigStatus')}
              </Typography>
              <Typography variant="h6" className="field">
                {spoolData.configStatus}
              </Typography>
            </Paper>
            <Paper className="paper" elevation={3}>
              <Typography className="header">
                {t('dashboard.maxSpoolAllowed')}
              </Typography>
              <Typography variant="h6" className="field">
                {spoolData.maxDiskUsage}
              </Typography>
            </Paper>
            <Paper className="paper" elevation={3}>
              <Typography className="header">
                {t('dashboard.currentSpoolUsage')}
              </Typography>
              <Typography variant="h6" className="field">
                {spoolData.currentPersistUsage}
              </Typography>
            </Paper>
          </>
        )}
      </div>
      
      {userRole?.roleName !== 'Admin' &&
        userRole?.roleName !== 'Contract Authority' && (
          <div style={{ padding: '1rem' }}>
            <Grid container spacing={2}>
              {queueData.length > 0 ? (
                queueData.map((queue, index) => (
                  <Grid item xs={12} key={index}>
                    <Card className="card-shadow">
                      <CardContent className="card-content">
                        <Typography
                          variant="h6"
                          component="div"
                          className="queue-header"
                          gutterBottom
                        >
                          {t('dashboard.queue', { name: queue.data.queueName })}
                          {queue.data.hasResourceInformationConflict && (
                            <Tooltip title={t('dashboard.resourceInformationConflictTooltip')}>
                              <Box component="span" className={classes.conflictIndicator}>
                                <WarningIcon fontSize="small" />
                                <Typography component="span" color="inherit" variant="body2">
                                  {t('dashboard.resourceInformationConflict')}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Typography>
                        <Divider style={{ marginBottom: '1rem' }} />
                        
                        <Grid container spacing={2}>
                          {/* First column - Basic queue info */}
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>{t('dashboard.queueInformation')}</Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.type')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {queue.data.accessType}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.ttl')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.maxTtl)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.ttlEnabled')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {queue.data.respectTtlEnabled ? t('dashboard.yes') : t('dashboard.no')}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.maxConsumersAllowed')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.maxBindCount)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.messagesQueued')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                {formatNumber(queue.collections?.msgs?.count ?? 0)}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.queueDepth')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                {formatNumber(queue.data.spooledMsgCount)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.spoolMessagesQueued')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.msgSpoolUsage)}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.expiredMessages')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.maxTtlExpiredDiscardedMsgCount)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {t('dashboard.spoolMaxAllowed')}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.maxMsgSpoolUsage)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          
                          {/* Second column - Performance metrics */}
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>{t('dashboard.performanceMetrics')}</Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.currentReceivedByteRatePerSecond')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.rxByteRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.currentReceivedMessageRatePerSecond')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.rxMsgRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.currentTransmitByteRatePerSecond')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.txByteRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.currentTransmitMessageRatePerSecond')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.txMsgRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={12}><Divider style={{ margin: '0.5rem 0' }}/></Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.averageReceivedByteRatePerMinute')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.averageRxByteRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.averageReceivedMessageRatePerMinute')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.averageRxMsgRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.averageTransmittedByteRatePerMinute')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.averageTxByteRate)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={7}>
                                <Typography variant="body2">
                                  {t('dashboard.averageTransmittedMessageRatePerMinute')}
                                </Typography>
                              </Grid>
                              <Grid item xs={5}>
                                <Typography variant="body2" className="field">
                                  {formatNumber(queue.data.averageTxMsgRate)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          
                          {/* Third column - Consumers */}
                          <Grid item xs={12} md={4}>
                            <Typography variant="subtitle1" gutterBottom>{t('dashboard.consumers')}</Typography>
                            
                            {queue.data.txFlowsData && queue.data.txFlowsData.data.length > 0 ? (
                              <>
                                <Grid item xs={12}>
                                  <Typography variant="body2">
                                    {t('dashboard.numberOfConnections')}
                                    <span className="field" style={{ marginLeft: '8px' }}>
                                      {formatNumber(queue.data.txFlowsData.data.length)}
                                    </span>
                                  </Typography>
                                </Grid>
                                <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '8px' }}>
                                  {queue.data.txFlowsData.data.map((consumer, idx) => (
                                    <Card key={idx} variant="outlined" style={{ margin: '0.5rem 0', padding: '0.5rem' }}>
                                      <Grid container spacing={1}>
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.clientName')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field" style={{ wordBreak: 'break-word' }}>
                                            {consumer.clientName}
                                          </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.connectionState')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field">
                                            {consumer.activityState}
                                          </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.activationTime')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field">
                                            {formatUtcDateTime(consumer.activationTime * 1000, true)}
                                          </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.bindTime')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field">
                                            {formatUtcDateTime(consumer.bindTime * 1000, true)}
                                          </Typography>
                                        </Grid>
                                        
                                        {/* Added Acknowledged Message Count */}
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.ackedMessageCount')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field">
                                            {formatNumber(consumer.storeAndForwardAckedMsgCount || 0)}
                                          </Typography>
                                        </Grid>
                                        
                                        {/* Additional message stats for better visibility */}
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.totalAckedMessages')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field">
                                            {formatNumber(consumer.ackedMsgCount || 0)}
                                          </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={5}>
                                          <Typography variant="body2">
                                            {t('dashboard.unackedMessages')}
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                          <Typography variant="body2" className="field">
                                            {formatNumber(consumer.unackedMsgCount || 0)}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Card>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <Typography variant="body2">{t('dashboard.numberOfConnections')}</Typography>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Paper
                  className="paper"
                  elevation={12}
                  style={{ margin: '0.5rem' }}
                >
                  <Typography>{t('dashboard.noQueueDetails')}</Typography>
                </Paper>
              )}
            </Grid>
          </div>
        )}
      
      {(userRole?.roleName === 'Admin' ||
        userRole?.roleName === 'Contract Authority') && (
        <Card style={{ margin: '1rem' }}>
          <CardContent>
            <Grid container spacing={2} style={{ alignItems: 'start' }}>
              <Grid item xs={12} md={4} className="gaugeGrid">
                <div className="gaugeContainer">
                  <Gauge
                    value={getGaugeValue(data.connections, data.maxConnections)}
                    width={200}
                    height={200}
                    sx={theme => ({
                      '& text': {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 40,
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                      },
                    })}
                  />
                  <div className="connectionsInfo">
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.totalClientConnections')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.connections)}
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.maxClientConnections')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.maxConnections)}
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={4} className="gaugeGrid">
                <div className="gaugeContainer">
                  <Gauge
                    value={getGaugeValue(
                      data.connectionsServiceAmqp,
                      data.maxConnectionsServiceAmqp,
                    )}
                    width={200}
                    height={200}
                    sx={theme => ({
                      '& text': {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 40,
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                      },
                    })}
                  />
                  <div className="connectionsInfo">
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.totalAmqpConnections')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.connectionsServiceAmqp)}
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.maxAmqpConnections')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.maxConnectionsServiceAmqp)}
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={4} className="gaugeGrid">
                <div className="gaugeContainer">
                  <Gauge
                    value={getGaugeValue(
                      data.currentIngressByteRatePerSecond,
                      data.currentEgressByteRatePerSecond,
                    )}
                    width={200}
                    height={200}
                    sx={theme => ({
                      '& text': {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 40,
                        color: '#FF0000',
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                      },
                    })}
                  />
                  <div className="connectionsInfo">
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.currentReceivedByteRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.currentIngressByteRatePerSecond)}
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.currentTransmitByteRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.currentEgressByteRatePerSecond)}
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={4} className="gaugeGrid">
                <div className="gaugeContainer">
                  <Gauge
                    value={getGaugeValue(
                      data.currentIngressRatePerSecond,
                      data.currentEgressRatePerSecond,
                    )}
                    width={200}
                    height={200}
                    sx={theme => ({
                      '& text': {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 40,
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                      },
                    })}
                  />
                  <div className="connectionsInfo">
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.currentReceivedMessageRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.currentIngressRatePerSecond)}
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.currentTransmitMessageRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.currentEgressRatePerSecond)}
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={4} className="gaugeGrid">
                <div className="gaugeContainer">
                  <Gauge
                    value={getGaugeValue(
                      data.averageIngressByteRatePerSecond,
                      data.averageEgressByteRatePerSecond,
                    )}
                    width={200}
                    height={200}
                    sx={theme => ({
                      '& text': {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 40,
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                      },
                    })}
                  />
                  <div className="connectionsInfo">
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.averageReceivedByteRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.averageIngressByteRatePerSecond)}
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.averageTransmitByteRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.averageEgressByteRatePerSecond)}
                    </Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={4} className="gaugeGrid">
                <div className="gaugeContainer">
                  <Gauge
                    value={getGaugeValue(
                      data.averageIngressRatePerSecond,
                      data.averageEgressRatePerSecond,
                    )}
                    width={200}
                    height={200}
                    sx={theme => ({
                      '& text': {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 40,
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: '#006eb0',
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                      },
                    })}
                  />
                  <div className="connectionsInfo">
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.averageReceivedMessageRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.averageIngressRatePerSecond)}
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: 'left' }}>
                      {t('dashboard.averageTransmitMessageRate')}
                    </Typography>
                    <Typography variant="h6" style={{ color: '#006eb0' }}>
                      {formatNumber(data.averageEgressRatePerSecond)}
                    </Typography>
                  </div>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default Dashboard;
